/**
 * useFileActions — the access/sharing actions for the app shell ⋮ and the
 * workspace tab ⋮ (#437), built from the ONE catalog in `@papan/files/ui` and
 * returned alongside the dialogs they open.
 *
 * A HOOK rather than a component because the two consumers render in different
 * chrome: AppWindow uses ToolbarPopover + ws-menu-item buttons, TabBar uses the
 * x/y-positioned FileContextMenu. Each renders `items` its own way and drops
 * `dialogs` into its tree.
 *
 * It lives in @papan/collab because that is the innermost package that can
 * reach all four dialog owners (files, share-links, org-connections). It must
 * NOT move to @papan/files — that package deliberately has no edge to collab.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from '@papan/i18n'
import { buildFileActions, canManageAccessFor, canWriteFor, type FileActionHandlers, type FileActionId, type FileItem } from '@papan/files/ui'
import { WhoHasAccessDialog, ShareInternallyDialog, DebugLogViewer } from '@papan/files/ui'
import { usePermission } from '@papan/files/client/queries'
import { ShareLinksDialog } from '@papan/share-links/ui'
import { isAppEligibleForFile } from '@papan/share-links/client'
import { ShareDialog } from '@papan/org-connections/ui'
import type { FileMenuItem } from '@papan/core-ui/ui'

type Dialog = null | 'who' | 'internal' | 'drive' | 'link' | 'debug'

export interface FileActionsTarget {
  fileId: string
  name: string
  path: string | null
  isFolder: boolean
  tenantId: string | undefined
  fileType?: string | null
  surface: 'app-shell' | 'tab'
  /** App shell, Mode B only. Omitted ⇒ Edit app is not offered. */
  onEditApp?: () => void
}

export function useFileActions(target: FileActionsTarget): { items: FileMenuItem[]; dialogs: ReactNode } {
  const { t } = useTranslation('files')
  const [dialog, setDialog] = useState<Dialog>(null)
  const perm = usePermission(target.fileId)
  const isOrg = !!target.tenantId?.startsWith('o_')
  const path = target.path ?? `/papan/${target.name}`

  const items = useMemo<FileMenuItem[]>(() => {
    const handlers: FileActionHandlers = {
      whoHasAccess: () => setDialog('who'),
      shareInternally: () => setDialog('internal'),
      shareOrg: () => setDialog('drive'),
      shareLink: () => setDialog('link'),
      debugLog: () => setDialog('debug'),
      // Edit app is a WRITE action and shares the "Develop" section with Debug
      // log, so gate it on write here — the catalog gates editApp on
      // isNativeMiniapp alone (no permission check), which would otherwise show
      // it to a read-only viewer. A read-only viewer sees neither Develop item.
      ...(target.surface === 'app-shell' && target.onEditApp && canWriteFor(perm.data) ? { editApp: () => target.onEditApp?.() } : {}),
    }
    const file: FileItem = {
      id: target.fileId,
      name: target.name,
      parentId: null,
      type: 'unknown',
      isFolder: target.isFolder,
      size: 0,
      mimeType: null,
      updatedAt: '',
      fileType: target.fileType ?? null,
    }
    const catalog = buildFileActions(file, {
      t,
      isOrg,
      canWrite: true,
      permission: perm.data,
      // An unresolved permission maps to pending for ANY reason it hasn't
      // resolved to a value — loading OR errored (retries exhausted, a 500).
      // A settled error leaves perm.data undefined and perm.isLoading false,
      // which without isError would fall through the catalog's own opt-out
      // path (permission undefined && permissionPending !== true => gated
      // = false => coarse, permissive mode) and render whoHasAccess/debugLog
      // ENABLED to any caller, including a viewer — contradicting the design
      // spec's 'a failed permission fetch is treated as unresolved, never as
      // granted.' perm.isError covers exactly that settled-error case.
      permissionPending: perm.isLoading || perm.isError,
      handlers,
    })
    // Present the shared actions in explicit SECTIONS, in the drive's order —
    // NOT the catalog's flat group order. The catalog itself can't be reordered
    // (its characterization pin protects the mobile/project-card order), so the
    // ordering + section headers live here. Two sections:
    //   • Share    — Who has access · Share internally · Share to another Drive
    //                · Share link (the drive's own sequence)
    //   • Develop  — Edit app · Debug log; write-access-only authoring tools, so
    //                a read-only viewer never sees this section at all.
    const pick = (id: FileActionId) => catalog.find((a) => a.id === id)
    const toItem = (a: NonNullable<ReturnType<typeof pick>>): FileMenuItem => ({
      kind: 'item', icon: a.icon, label: a.label, disabled: a.disabled, onClick: a.run,
    })
    const section = (ids: FileActionId[]) =>
      ids.map(pick).filter((a): a is NonNullable<typeof a> => !!a).map(toItem)

    const shareItems = section(['whoHasAccess', 'shareInternally', 'shareOrg', 'shareLink'])
    const devItems = section(['editApp', 'debugLog'])

    const out: FileMenuItem[] = []
    if (shareItems.length) out.push({ kind: 'header', label: 'Share' }, ...shareItems)
    if (devItems.length) {
      if (out.length) out.push({ kind: 'separator' })
      out.push({ kind: 'header', label: 'Develop' }, ...devItems)
    }
    return out
    // Depend on the primitive fields, NOT `target` — callers construct it
    // inline (a new object every render), so keying on the object defeats the
    // memo. The fields are stable primitives (onEditApp aside).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.fileId, target.name, target.isFolder, target.fileType, target.surface, target.onEditApp, t, isOrg, perm.data, perm.isLoading, perm.isError])

  const close = () => setDialog(null)
  const dialogs = (
    <>
      {dialog === 'who' && target.tenantId && (
        <WhoHasAccessDialog
          folderId={target.fileId}
          folderName={target.name}
          folderPath={path}
          tenantId={target.tenantId}
          isFolder={target.isFolder}
          myPermission={perm.data}
          // A cross-tenant `.shared` mount: surface the source-cap note and the
          // share-context Manage & Edit tier, matching the drive's own dialog.
          isReceivedShare={target.fileType === 'shared'}
          inShareContext={target.fileType === 'shared'}
          // Personal drives have no roles/teams — hide that tab and default to
          // Links. The dialog still shows Links / Outsiders / Apps there.
          hidePeopleTeams={!isOrg}
          onClose={close}
        />
      )}
      {dialog === 'internal' && target.tenantId && (
        <ShareInternallyDialog
          fileId={target.fileId}
          fileName={target.name}
          tenantId={target.tenantId}
          isOrg={isOrg}
          isFolder={target.isFolder}
          onClose={close}
        />
      )}
      {dialog === 'drive' && (
        <ShareDialog
          folderId={target.fileId}
          folderName={target.name}
          folderPath={path}
          isFolder={target.isFolder}
          myPermission={perm.data}
          // Existing recipient rows are editable only for a manager — matching
          // the drive, which passes rowCanManageAccess. Defaults to true, so
          // without this a read-write member could edit them here but not there.
          canManage={canManageAccessFor(perm.data)}
          onClose={close}
        />
      )}
      {dialog === 'link' && (
        <ShareLinksDialog
          fileId={target.fileId}
          fileName={target.name}
          filePath={path}
          isFolder={target.isFolder}
          isAppEligible={isAppEligibleForFile({ name: target.name, isFolder: target.isFolder })}
          onClose={close}
        />
      )}
      {dialog === 'debug' && (
        <DebugLogViewer
          file={{ id: target.fileId, name: target.name, isFolder: target.isFolder }}
          onClose={close}
        />
      )}
    </>
  )

  return { items, dialogs }
}
